import { ArrowRightLeft, Ban, BellRing, Clock3, Copy, Eye, Edit3, GitMerge, Plus, Printer, QrCode, ReceiptText, RotateCcw, ShoppingBag, Trash2, Users, WalletCards, X } from 'lucide-react'
import { useEffect, useMemo, useState, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { useLiveTable } from '../hooks/useLiveTable'
import { supabase } from '../lib/supabase'
import { getCurrentEstablishmentId } from '../lib/tenant'

type TableStatus = 'free' | 'reserved' | 'occupied' | 'ordering' | 'preparing' | 'ready' | 'service_requested' | 'bill_requested' | 'awaiting_payment' | 'closed'
type DiningTable = { id: string; number: number; name: string | null; capacity: number; status: TableStatus; qr_token:string; updated_at:string; bill_requested_at:string|null; bill_printed_at:string|null; opened_at:string|null; closed_at:string|null; released_at:string|null; qr_open_allowed_until:string|null; customer_id:string|null; reservation_name:string|null; reservation_phone:string|null }
type TableForm = { number: string; name: string; capacity: string; status: TableStatus }
type TableSummary = { total:number; paid:number; balance:number; items:number; orders:number; openedAt:string|null }
type CustomerMini = { id:string; name:string; active:boolean }
type CommandMini = { id:string; table_id:string; command_number:number; customer_id:string|null; customer_name:string|null; mode:string; status:string }

const emptyForm: TableForm = { number: '', name: '', capacity: '4', status: 'free' }
const PUBLIC_GUEST_ORIGIN = 'https://goncalo.unicasites.com.br'
const GUEST_QR_ENABLED = false
const statusLabels: Record<TableStatus, string> = {
  free: 'Livre', reserved: 'Reservada', occupied: 'Ocupada', ordering: 'Fazendo pedido',
  preparing: 'Em preparo', ready: 'Pedido pronto', service_requested: 'Chamando garçom',
  bill_requested: 'Conta solicitada', awaiting_payment: 'Aguardando pagamento', closed: 'Fechada',
}

export function Tables() {
  const navigate=useNavigate()
  const { rows, loading, error, reload } = useLiveTable<DiningTable>('dining_tables', 'number')
  const { rows:customers } = useLiveTable<CustomerMini>('customers','name')
  const { rows:commands } = useLiveTable<CommandMini>('table_commands','command_number')
  const [open, setOpen] = useState(false)
  const [editing, setEditing] = useState<DiningTable | null>(null)
  const [form, setForm] = useState<TableForm>(emptyForm)
  const [formError, setFormError] = useState('')
  const [saving, setSaving] = useState(false)
  const [qrTable,setQrTable]=useState<DiningTable|null>(null)
  const [copied,setCopied]=useState(false)
  const [reopening,setReopening]=useState<string|null>(null)
  const [bulkReopening,setBulkReopening]=useState(false)
  const [closing,setClosing]=useState<string|null>(null)
  const [summaries,setSummaries]=useState<Record<string,TableSummary>>({})
  const [orderCustomerNames,setOrderCustomerNames]=useState<Record<string,string[]>>({})
  const [movingTable,setMovingTable]=useState<DiningTable|null>(null)
  const [moveMode,setMoveMode]=useState<'transfer'|'merge'>('transfer')
  const [targetTableId,setTargetTableId]=useState('')
  const [moveNotes,setMoveNotes]=useState('')
  const [moveSaving,setMoveSaving]=useState(false)
  const [operationNotice,setOperationNotice]=useState('')
  const [armingQr,setArmingQr]=useState<string|null>(null)
  const [unnumbered,setUnnumbered]=useState(false)
  const [reservationTable,setReservationTable]=useState<DiningTable|null>(null)
  const [reservationName,setReservationName]=useState('')
  const [reservationPhone,setReservationPhone]=useState('')
  const [reservationSaving,setReservationSaving]=useState(false)
  const [reservationError,setReservationError]=useState('')

  useEffect(() => {
    if (!open) {
      setEditing(null)
      setForm(emptyForm)
      setFormError('')
    }
  }, [open])

  function openReservation(table:DiningTable){setReservationTable(table);setReservationName('');setReservationPhone('');setReservationError('')}
  async function manageReservation(table:DiningTable,action:'reserve'|'cancel'|'arrive'){
    if(action==='cancel'&&!window.confirm('Cancelar a reserva da '+tableFullLabel(table)+'?'))return
    setReservationSaving(true);setReservationError('')
    const {error:reservationIssue}=await supabase.rpc('manage_table_reservation',{p_table_id:table.id,p_action:action,p_name:action==='reserve'?reservationName.trim():null,p_phone:action==='reserve'?reservationPhone.trim():null})
    if(reservationIssue){setReservationError(reservationIssue.message);if(action!=='reserve')window.alert(reservationIssue.message)}
    else{setReservationTable(null);await reload()}
    setReservationSaving(false)
  }

  function createTable() {
    setEditing(null)
    setForm(emptyForm)
    setUnnumbered(false)
    setOpen(true)
  }

  function editTable(table: DiningTable) {
    setEditing(table)
    const normalizedName=table.name?.trim().toLocaleLowerCase('pt-BR')||''
    const isUnnumbered=normalizedName.startsWith('sem número')
    const unnumberedReference=isUnnumbered?(table.name?.split('·').slice(1).join('·').trim()||''):''
    setUnnumbered(isUnnumbered)
    setForm({ number: isUnnumbered?'':String(table.number), name: isUnnumbered?unnumberedReference:(table.name || ''), capacity: String(table.capacity), status: table.status })
    setOpen(true)
  }

  async function save(event: FormEvent) {
    event.preventDefault()
    setFormError('')
    setSaving(true)

    try {
      const generatedNumber=editing?.number??(Math.max(1000,...rows.map(table=>table.number))+1)
      const payload = {
        number: unnumbered ? generatedNumber : Number(form.number),
        name: unnumbered ? `Sem número · ${form.name.trim()}` : (form.name.trim() || null),
        capacity: Number(form.capacity),
        status: form.status,
        updated_at: new Date().toISOString(),
      }

      if (!unnumbered && (!Number.isInteger(payload.number) || payload.number < 1)) throw new Error('Informe um número de mesa válido.')
      if (unnumbered && (!/^\d{1,4}$/.test(form.name.trim()) || Number(form.name) < 1)) throw new Error('Informe um número aleatório de identificação para a mesa sem número.')
      if (!Number.isInteger(payload.capacity) || payload.capacity < 1) throw new Error('A capacidade deve ser maior que zero.')

      if (editing) {
        const { error: updateError } = await supabase.from('dining_tables').update(payload).eq('id', editing.id)
        if (updateError) throw updateError
      } else {
        const establishmentId = await getCurrentEstablishmentId()
        const { error: insertError } = await supabase.from('dining_tables').insert({ ...payload, establishment_id: establishmentId })
        if (insertError) throw insertError
      }

      await reload()
      setOpen(false)
    } catch (caught) {
      const message = caught instanceof Error ? caught.message : 'Não foi possível salvar a mesa.'
      setFormError(message.includes('duplicate key') ? 'Já existe uma mesa com esse número.' : message)
    } finally {
      setSaving(false)
    }
  }

  const unnumberedRef=(table:DiningTable)=>{const name=table.name?.trim()||'';if(!name.toLocaleLowerCase('pt-BR').startsWith('sem número'))return '';return name.split('·').slice(1).join('·').trim()}
  const tableNumberLabel=(table:DiningTable)=>table.name?.trim().toLocaleLowerCase('pt-BR').startsWith('sem número')?`Sem nº${unnumberedRef(table)?' '+unnumberedRef(table):''}`:String(table.number).padStart(2,'0')
  const tableFullLabel=(table:DiningTable)=>table.name?.trim().toLocaleLowerCase('pt-BR').startsWith('sem número')?`Mesa sem número${unnumberedRef(table)?' '+unnumberedRef(table):''}`:`Mesa ${tableNumberLabel(table)}`

  async function remove(table: DiningTable) {
    if (!window.confirm(`Excluir ${tableFullLabel(table)}? Essa ação não poderá ser desfeita.`)) return
    const { error: deleteError } = await supabase.from('dining_tables').delete().eq('id', table.id)
    if (deleteError) {
      window.alert(deleteError.message.includes('foreign key') ? 'A mesa possui pedidos vinculados e não pode ser excluída.' : deleteError.message)
      return
    }
    await reload()
  }

  async function reopen(table:DiningTable){
    setReopening(table.id)
    const {data:{user}}=await supabase.auth.getUser()
    if(!user){window.alert('Sua sessão expirou. Entre novamente.');setReopening(null);return}
    const {error:rpcError}=await supabase.rpc('reopen_table',{p_table_id:table.id,p_actor_id:user.id})
    if(rpcError) window.alert(rpcError.message)
    await reload()
    setReopening(null)
  }


  async function reopenAllClosed(){
    if(bulkReopening||reopening)return
    const closed=rows.filter(table=>table.status==='closed')
    if(!closed.length){window.alert('Não há mesas fechadas para reabrir.');return}
    if(!window.confirm('Reabrir '+closed.length+' mesa(s) fechada(s) de uma vez? Esta ação muda o status das mesas, mas não estorna pagamentos nem reabre comandas individuais já quitadas.'))return
    setBulkReopening(true);setOperationNotice('')
    const {data:{user}}=await supabase.auth.getUser()
    if(!user){window.alert('Sua sessão expirou. Entre novamente.');setBulkReopening(false);return}
    let success=0
    const failed:string[]=[]
    for(const table of closed){
      const {error:issue}=await supabase.rpc('reopen_table',{p_table_id:table.id,p_actor_id:user.id})
      if(issue)failed.push('Mesa '+table.number+': '+issue.message)
      else success++
    }
    await reload()
    setBulkReopening(false)
    setOperationNotice(success+' mesa(s) reaberta(s).'+(failed.length?' Falhas: '+failed.join(' | '):''))
  }

  async function loadSummaries(){
    const {data,error:summaryError}=await supabase.from('orders')
      .select('id,table_id,total,status,created_at,customer_name,command_customer_name,customer_id,payments(amount),order_items(quantity)')
      .not('status','in','(draft,cancelled,closed)')
    if(summaryError)return
    const next:Record<string,TableSummary>={}
    const namesByTable:Record<string,string[]>={}
    for(const order of (data||[]) as any[]){
      if(!order.table_id)continue
      const orderName=String(order.command_customer_name||order.customer_name||'').trim()
      if(orderName){
        const names=namesByTable[order.table_id]||(namesByTable[order.table_id]=[])
        if(!names.includes(orderName))names.push(orderName)
      }
      const current=next[order.table_id]||{total:0,paid:0,balance:0,items:0,orders:0,openedAt:null}
      current.total+=Number(order.total||0)
      current.paid+=(order.payments||[]).reduce((s:number,p:any)=>s+Number(p.amount||0),0)
      current.items+=(order.order_items||[]).reduce((s:number,i:any)=>s+Number(i.quantity||0),0)
      current.orders+=1
      if(!current.openedAt||new Date(order.created_at).getTime()<new Date(current.openedAt).getTime())current.openedAt=order.created_at
      current.balance=Math.max(0,current.total-current.paid)
      next[order.table_id]=current
    }
    setSummaries(next)
    setOrderCustomerNames(namesByTable)
  }

  useEffect(()=>{
    void loadSummaries()
    const channel=supabase.channel('live:table-summaries')
      .on('postgres_changes',{event:'*',schema:'public',table:'orders'},()=>void loadSummaries())
      .on('postgres_changes',{event:'*',schema:'public',table:'payments'},()=>void loadSummaries())
      .on('postgres_changes',{event:'*',schema:'public',table:'order_items'},()=>void loadSummaries())
      .subscribe()
    return()=>{void supabase.removeChannel(channel)}
  },[])

  function elapsed(openedAt:string|null){
    if(!openedAt)return ''
    const mins=Math.max(0,Math.floor((Date.now()-new Date(openedAt).getTime())/60000))
    if(mins<60)return `${mins} min`
    const h=Math.floor(mins/60),m=mins%60
    return `${h}h${m?String(m).padStart(2,'0')+'min':''}`
  }

  async function requestBill(table:DiningTable){
    if(!window.confirm(`Solicitar o fechamento da ${tableFullLabel(table)}? A conta será enviada para impressão no Caixa.`))return
    setClosing(table.id)
    const {error:updateError}=await supabase.from('dining_tables').update({status:'bill_requested',updated_at:new Date().toISOString()}).eq('id',table.id)
    if(updateError)window.alert(updateError.message)
    await reload()
    setClosing(null)
  }

  function startTableOperation(table:DiningTable){
    setMovingTable(table)
    setMoveMode('transfer')
    setTargetTableId('')
    setMoveNotes('')
    setOperationNotice('')
  }

  function operationTargets(source:DiningTable,mode:'transfer'|'merge'){
    return rows.filter(table=>{
      if(table.id===source.id)return false
      if(mode==='transfer')return visualGroup(table)==='free'||table.status==='reserved'
      return !['closed','bill_requested','awaiting_payment'].includes(table.status)
    }).sort((a,b)=>a.number-b.number)
  }

  async function submitTableOperation(event:FormEvent){
    event.preventDefault()
    if(!movingTable||!targetTableId)return
    const target=rows.find(table=>table.id===targetTableId)
    if(!target)return
    const action=moveMode==='transfer'?'transfer_table':'merge_tables'
    const label=moveMode==='transfer'?'Transferência':'Junção'
    const question=moveMode==='transfer'
      ?'Transferir todo o atendimento da '+tableFullLabel(movingTable)+' para a '+tableFullLabel(target)+'?'
      :'Juntar o consumo da '+tableFullLabel(movingTable)+' na '+tableFullLabel(target)+'?'
    if(!window.confirm(question))return
    setMoveSaving(true)
    try{
      const {data,error:rpcError}=await supabase.rpc(action,{
        p_source_table_id:movingTable.id,
        p_target_table_id:target.id,
        p_notes:moveNotes.trim()||null,
      })
      if(rpcError){
        const networkError=/failed to fetch|networkerror|load failed/i.test(rpcError.message)
        if(networkError){
          setOperationNotice('A conexão caiu durante a operação. Confira as mesas de origem e destino antes de tentar novamente; a transferência pode ter sido concluída.')
          setMovingTable(null)
          await Promise.allSettled([reload(),loadSummaries()])
        }else window.alert(rpcError.message)
      }else{
        setOperationNotice(label+' concluída: '+tableFullLabel(movingTable)+' → '+tableFullLabel(target)+'. '+Number(data?.orders_moved||0)+' pedido(s) movido(s).')
        setMovingTable(null)
        await Promise.allSettled([reload(),loadSummaries()])
      }
    }catch(caught){
      setOperationNotice('Não foi possível confirmar a '+label.toLowerCase()+'. Verifique as duas mesas antes de repetir a operação. '+(caught instanceof Error?caught.message:'Falha de conexão.'))
      setMovingTable(null)
      await Promise.allSettled([reload(),loadSummaries()])
    }finally{
      setMoveSaving(false)
    }
  }
  async function armQr(table:DiningTable){
    setArmingQr(table.id)
    setOperationNotice('')
    const {data,error:rpcError}=await supabase.rpc('arm_table_qr',{p_table_id:table.id,p_minutes:10})
    if(rpcError)window.alert(rpcError.message)
    else{
      const until=data?.allowed_until?new Date(data.allowed_until).toLocaleTimeString('pt-BR',{hour:'2-digit',minute:'2-digit'}):''
      setOperationNotice(`Mesa ${table.number}: QR liberado por 10 minutos${until?` (até ${until})`:''}. O primeiro cliente que iniciar o atendimento recebe a sessão desta mesa.`)
      await reload()
    }
    setArmingQr(null)
  }

  function openQr(table:DiningTable) { setQrTable(table); setCopied(false) }
  function guestLink(table:DiningTable) { return `${PUBLIC_GUEST_ORIGIN}/mesa/${table.qr_token}` }
  async function copyLink() {
    if(!qrTable) return
    await navigator.clipboard.writeText(guestLink(qrTable))
    setCopied(true)
  }

  function hasActiveConsumption(table:DiningTable){
    const summary=summaries[table.id]
    return Boolean(summary&&(summary.orders>0||summary.total>0||summary.items>0))
  }

  function visualGroup(table:DiningTable){
    if(table.status==='closed')return 'closed'
    if(['service_requested','bill_requested','awaiting_payment'].includes(table.status))return 'attention'
    if(table.status==='free'&&!hasActiveConsumption(table))return 'free'
    if(table.status==='reserved')return 'reserved'
    return 'occupied'
  }

  function tableCustomerNames(table:DiningTable){
    const names:string[]=[]
    if(table.customer_id){
      const customer=customers.find(item=>item.id===table.customer_id)
      if(customer?.name)names.push(customer.name)
    }
    for(const command of commands){
      if(command.table_id!==table.id||command.status!=='open')continue
      const customerName=command.customer_id
        ? customers.find(item=>item.id===command.customer_id)?.name
        : command.customer_name
      if(customerName&&!names.includes(customerName))names.push(customerName)
    }
    for(const name of orderCustomerNames[table.id]||[]){
      if(!names.some(existing=>existing.toLocaleLowerCase('pt-BR')===name.toLocaleLowerCase('pt-BR')))names.push(name)
    }
    return names
  }

  function tableCommandNumbers(table:DiningTable){
    return commands
      .filter(command=>command.table_id===table.id&&command.status==='open')
      .map(command=>'#'+String(command.command_number).padStart(3,'0'))
  }

  function visualStatusLabel(table:DiningTable){
    if(table.status==='closed')return 'Saída liberada'
    if(table.status==='free'&&hasActiveConsumption(table))return 'Em consumo'
    if(['occupied','ordering','preparing','ready'].includes(table.status))return 'Em consumo'
    return statusLabels[table.status]||table.status
  }

  const priority:Record<TableStatus,number>={bill_requested:0,service_requested:1,awaiting_payment:2,ready:3,preparing:4,ordering:5,occupied:6,reserved:7,free:8,closed:9}
  const sortedRows=useMemo(()=>[...rows].sort((a,b)=>(priority[a.status]??99)-(priority[b.status]??99)||a.number-b.number),[rows])
  const counts=useMemo(()=>({
    free:rows.filter(t=>visualGroup(t)==='free').length,
    active:rows.filter(t=>visualGroup(t)==='occupied').length,
    attention:rows.filter(t=>visualGroup(t)==='attention').length,
    payment:rows.filter(t=>['bill_requested','awaiting_payment'].includes(t.status)).length,
  }),[rows,summaries])

  return <>
    <div className="page-heading">
      <div><span className="eyebrow dark">SALÃO</span><h1>Mapa de mesas</h1><p>Visão instantânea da ocupação e do atendimento.</p></div>
      <div style={{display:'flex',gap:8,flexWrap:'wrap',alignItems:'center'}}><button className="secondary-button small" onClick={()=>void reopenAllClosed()} disabled={bulkReopening||!!reopening||!rows.some(table=>table.status==='closed')}><RotateCcw size={17}/>{bulkReopening?'Reabrindo mesas…':'Reabrir todas as fechadas'}</button><button className="primary-button small" onClick={createTable}><Plus size={18}/> Nova mesa</button></div>
    </div>

    {error && <div className="form-error">{error}</div>}
    {operationNotice&&<div className="notice">{operationNotice}</div>}
    {!loading&&rows.length>0&&<div className="metric-grid" style={{marginBottom:18}}><article className="metric-card"><span>Mesas livres</span><strong>{counts.free}</strong><small>Disponíveis para novos clientes</small><div className="metric-icon"><Users size={20}/></div></article><article className="metric-card"><span>Em atendimento</span><strong>{counts.active}</strong><small>Com operação em andamento</small><div className="metric-icon"><QrCode size={20}/></div></article><article className="metric-card"><span>Precisam atenção</span><strong>{counts.attention}</strong><small>Garçom ou fechamento solicitado</small><div className="metric-icon"><BellRing size={20}/></div></article><article className="metric-card"><span>Aguardando pagamento</span><strong>{counts.payment}</strong><small>Conta solicitada ou impressa</small><div className="metric-icon"><ReceiptText size={20}/></div></article></div>}
    {loading ? <div className="loading">Carregando mesas…</div> : rows.length ? <div className="tables-grid">
      {sortedRows.map(table => {
        const summary=summaries[table.id]
        const group=visualGroup(table)
        const openedAt=table.opened_at||summary?.openedAt||null
        const closedAt=table.released_at||table.closed_at||null
        return <article className={`table-card table-card-${group} status-${table.status}`} key={table.id}>
        <div className="card-actions">
          {GUEST_QR_ENABLED&&<button aria-label="Abrir QR Code" onClick={() => openQr(table)}><QrCode size={16}/></button>}
          {group!=='free'&&group!=='closed'&&<button aria-label="Transferir ou juntar mesa" title="Transferir / juntar mesa" onClick={()=>startTableOperation(table)}><ArrowRightLeft size={16}/></button>}
          <button aria-label="Editar mesa" onClick={() => editTable(table)}><Edit3 size={16}/></button>
          <button className="danger-action" aria-label="Excluir mesa" onClick={() => void remove(table)}><Trash2 size={16}/></button>
        </div>
        <div><span>MESA</span><strong style={tableNumberLabel(table)==='Sem número'?{fontSize:'1.45rem'}:undefined}>{tableNumberLabel(table)}</strong></div>
        <small><Users size={14}/>{table.capacity} lugares</small>
        {group!=='free'&&group!=='closed'&&tableCustomerNames(table).length>0&&<div className="table-customer-chip" style={{fontSize:16,fontWeight:900,padding:'8px 10px',lineHeight:1.25,overflowWrap:'anywhere'}}><Users size={17}/><span>{tableCustomerNames(table).slice(0,2).join(' / ')}{tableCustomerNames(table).length>2?` +${tableCustomerNames(table).length-2}`:''}</span></div>}
        {group!=='free'&&tableCommandNumbers(table).length>0&&<div className="table-command-chip"><ReceiptText size={13}/><span>{tableCommandNumbers(table).slice(0,3).join(' · ')}{tableCommandNumbers(table).length>3?' …':''}</span></div>}
        <div className={`table-status-pill ${group}`}>{visualStatusLabel(table)}</div>
        {GUEST_QR_ENABLED&&group==='free'&&<button className="table-arm-qr-button" onClick={()=>void armQr(table)} disabled={armingQr===table.id}><QrCode size={15}/>{armingQr===table.id?'Liberando…':table.qr_open_allowed_until&&new Date(table.qr_open_allowed_until).getTime()>Date.now()?'QR liberado':'Liberar QR · 10 min'}</button>}
        {group==='occupied'&&summary&&<div className="table-live-summary compact table-money-panel">
          <div className="table-money-item table-money-consumption"><span>Consumo total</span><strong>R$ {summary.total.toFixed(2).replace('.',',')}</strong></div>
          <div className="table-money-item table-money-paid"><span>Já pago</span><strong>R$ {summary.paid.toFixed(2).replace('.',',')}</strong></div>
          <div className="table-money-item table-money-due"><span>Falta receber</span><strong>R$ {summary.balance.toFixed(2).replace('.',',')}</strong></div>
          <div className="table-summary-meta"><Clock3 size={14}/><span>{elapsed(openedAt)}</span><span>·</span><ShoppingBag size={14}/><span>{summary.orders} pedido(s)</span></div>
        </div>}
        {group==='attention'&&<div className="table-live-summary compact table-money-panel attention-summary">
          <div className="table-money-item table-money-consumption"><span>Consumo total</span><strong>R$ {(summary?.total||0).toFixed(2).replace('.',',')}</strong></div>
          <div className="table-money-item table-money-paid"><span>Já pago</span><strong>R$ {(summary?.paid||0).toFixed(2).replace('.',',')}</strong></div>
          <div className="table-money-item table-money-due"><span>Falta receber</span><strong>R$ {(summary?.balance||0).toFixed(2).replace('.',',')}</strong></div>
          <div className="table-summary-meta"><Clock3 size={14}/><span>{elapsed(openedAt)}</span><span>·</span><ShoppingBag size={14}/><span>{summary?.orders||0} pedido(s)</span></div>
        </div>}
        {group==='closed'&&<div className="table-released-summary">
          <strong>✓ SAÍDA LIBERADA</strong>
          {closedAt&&<span>Fechada às {new Date(closedAt).toLocaleTimeString('pt-BR',{hour:'2-digit',minute:'2-digit'})}</span>}
        </div>}
        {table.status==='service_requested'&&<div className="table-attention-banner"><BellRing size={16}/> Cliente chamou o garçom</div>}
        {table.status==='bill_requested'&&<div className="table-attention-banner bill"><ReceiptText size={16}/> Conta solicitada{table.bill_requested_at?` às ${new Date(table.bill_requested_at).toLocaleTimeString('pt-BR',{hour:'2-digit',minute:'2-digit'})}`:''}</div>}
        {table.status==='awaiting_payment'&&<div className="table-attention-banner payment"><WalletCards size={16}/> Conta impressa · aguardando pagamento</div>}
        {group==='reserved'&&<div className="table-live-summary compact" style={{display:'grid',gap:6}}><strong>Reserva: {table.reservation_name||'Cliente não informado'}</strong><span>Telefone: {table.reservation_phone||'Não informado'}</span></div>}
        {group==='reserved'&&<><button type="button" className="primary-button small table-main-action" disabled={reservationSaving} onClick={()=>void manageReservation(table,'arrive')}><Users size={16}/> Cliente chegou</button><button type="button" className="secondary-button small table-main-action" disabled={reservationSaving} onClick={()=>void manageReservation(table,'cancel')}><X size={16}/> Cancelar reserva</button></>}
        {group==='free'&&<button type="button" className="secondary-button small table-main-action" onClick={()=>openReservation(table)}><Users size={16}/> Reservar mesa</button>}
        {visualGroup(table)==='free'&&<button type="button" className="primary-button small table-main-action" onClick={()=>navigate(`/pedidos?mesa=${encodeURIComponent(table.id)}`)}><Plus size={16}/> Adicionar pedido</button>}
        {!['free','reserved','closed','bill_requested','awaiting_payment'].includes(table.status)&&<><button className="secondary-button small table-main-action" onClick={()=>navigate(`/pedidos?mesa=${encodeURIComponent(table.id)}&acao=ver`)}><Eye size={16}/> Ver mesa</button><button className="primary-button small table-main-action" onClick={()=>navigate(`/pedidos?mesa=${encodeURIComponent(table.id)}`)}><Plus size={16}/> Adicionar pedido</button><button className="secondary-button small table-main-action" style={{borderColor:'#fff',color:'#fff',background:'rgba(90,0,0,.28)'}} onClick={()=>navigate(`/pedidos?mesa=${encodeURIComponent(table.id)}&acao=excluir`)}><Ban size={16}/> Excluir produto</button><button className="secondary-button small table-main-action" onClick={()=>void requestBill(table)} disabled={closing===table.id}><ReceiptText size={16}/>{closing===table.id?'Enviando…':'Solicitar conta'}</button></>}
        {table.status==='closed'&&<button className="secondary-button small" onClick={()=>void reopen(table)} disabled={bulkReopening||!!reopening}><RotateCcw size={16}/>{reopening===table.id?'Reabrindo…':'Reabrir mesa'}</button>}
      </article>})}
    </div> : <div className="panel empty-state"><Users size={36}/><strong>Nenhuma mesa cadastrada</strong><p>Cadastre a primeira mesa para iniciar a operação.</p></div>}

    {reservationTable&&<div className="modal-backdrop" role="presentation" onMouseDown={()=>!reservationSaving&&setReservationTable(null)}>
      <section className="modal-card" role="dialog" aria-modal="true" aria-labelledby="reservation-title" onMouseDown={event=>event.stopPropagation()}>
        <div className="modal-heading"><div><span className="eyebrow dark">RESERVA</span><h2 id="reservation-title">Reservar {tableFullLabel(reservationTable)}</h2></div><button type="button" className="icon-button" aria-label="Fechar" onClick={()=>setReservationTable(null)}><X size={20}/></button></div>
        <form className="entity-form" onSubmit={event=>{event.preventDefault();void manageReservation(reservationTable,'reserve')}}>
          <label>Nome do cliente *<input value={reservationName} onChange={event=>setReservationName(event.target.value)} required minLength={2} maxLength={120} placeholder="Nome da pessoa"/></label>
          <label>Telefone / WhatsApp *<input type="tel" value={reservationPhone} onChange={event=>setReservationPhone(event.target.value)} required minLength={8} maxLength={30} placeholder="(35) 99999-9999"/></label>
          {reservationError&&<p className="form-error">{reservationError}</p>}
          <button type="submit" className="primary-button" disabled={reservationSaving}>{reservationSaving?'Reservando…':'Confirmar reserva'}</button>
        </form>
      </section>
    </div>}
        {open && <div className="modal-backdrop" role="presentation" onMouseDown={() => !saving && setOpen(false)}>
      <section className="modal-card" role="dialog" aria-modal="true" aria-labelledby="table-form-title" onMouseDown={event => event.stopPropagation()}>
        <header><div><span className="eyebrow dark">SALÃO</span><h2 id="table-form-title">{editing ? 'Editar mesa' : 'Nova mesa'}</h2></div><button className="modal-close" onClick={() => setOpen(false)} aria-label="Fechar"><X/></button></header>
        <form className="entity-form" onSubmit={save}>
          <div className="form-grid">
            <label>{unnumbered?'Número aleatório':'Número'}{unnumbered?<input type="number" min="1" max="9999" value={form.name} onChange={e=>setForm({...form,name:e.target.value})} placeholder="Ex.: 57" required autoFocus/>:<input type="number" min="1" value={form.number} onChange={e => setForm({...form, number:e.target.value})} required autoFocus/>}</label>
            <label>Capacidade<input type="number" min="1" value={form.capacity} onChange={e => setForm({...form, capacity:e.target.value})} required/></label>
          </div>
          <label style={{display:'flex',alignItems:'center',gap:10,padding:'12px 14px',background:'#f5f5ef',borderRadius:10}}><input type="checkbox" checked={unnumbered} onChange={e=>{setUnnumbered(e.target.checked);if(e.target.checked)setForm({...form,number:'',name:''})}} style={{width:18,height:18}}/><span><strong>Mesa sem número</strong><small style={{display:'block',fontWeight:400}}>Pode cadastrar várias e escolher um número aleatório para identificar cada uma.</small></span></label>
          {!unnumbered&&<label>Nome opcional<input value={form.name} onChange={e => setForm({...form, name:e.target.value})} placeholder="Ex.: Varanda"/></label>}
          <label>Status<select value={form.status} onChange={e => setForm({...form, status:e.target.value as TableStatus})}>{Object.entries(statusLabels).map(([value,label]) => <option key={value} value={value}>{label}</option>)}</select></label>
          {formError && <div className="form-error" role="alert">{formError}</div>}
          <div className="form-actions"><button type="button" className="ghost-button" onClick={() => setOpen(false)}>Cancelar</button><button className="primary-button" disabled={saving}>{saving ? 'Salvando…' : 'Salvar mesa'}</button></div>
        </form>
      </section>
    </div>}
    {movingTable&&<div className="modal-backdrop" role="presentation" onMouseDown={()=>!moveSaving&&setMovingTable(null)}><section className="modal-card table-operation-modal" role="dialog" aria-modal="true" onMouseDown={event=>event.stopPropagation()}><header><div><span className="eyebrow dark">ORGANIZAÇÃO DO SALÃO</span><h2>{tableFullLabel(movingTable)}</h2></div><button className="modal-close" onClick={()=>setMovingTable(null)}><X/></button></header><form className="entity-form" onSubmit={submitTableOperation}>
<div className="table-operation-tabs">
<button type="button" className={moveMode==='transfer'?'active':''} onClick={()=>{setMoveMode('transfer');setTargetTableId('')}}><ArrowRightLeft size={18}/><strong>Transferir mesa</strong><small>Leva todo o atendimento para uma mesa livre.</small></button>
<button type="button" className={moveMode==='merge'?'active':''} onClick={()=>{setMoveMode('merge');setTargetTableId('')}}><GitMerge size={18}/><strong>Juntar mesas</strong><small>Une o consumo desta mesa a outra mesa em atendimento.</small></button>
</div>
<label>Mesa de destino<select value={targetTableId} onChange={e=>setTargetTableId(e.target.value)} required><option value="">Selecione a mesa</option>{operationTargets(movingTable,moveMode).map(table=><option key={table.id} value={table.id}>{tableFullLabel(table)} — {visualStatusLabel(table)}{summaries[table.id]?' · R$ '+summaries[table.id].total.toFixed(2).replace('.',','):''}</option>)}</select></label>
<label>Observação opcional<textarea rows={2} value={moveNotes} onChange={e=>setMoveNotes(e.target.value)} placeholder="Ex.: clientes mudaram para a área externa"/></label>
<div className="settings-tip"><ArrowRightLeft size={18}/><span>{moveMode==='transfer'?'Todos os pedidos, pagamentos já registrados e solicitações pendentes acompanham a mesa. A mesa de origem fica livre.':'Os consumos são reunidos na mesa de destino sem duplicar pedidos. A mesa de origem fica livre.'}</span></div>
<div className="form-actions"><button type="button" className="ghost-button" onClick={()=>setMovingTable(null)}>Cancelar</button><button className="primary-button" disabled={moveSaving||!targetTableId}>{moveSaving?'Processando…':moveMode==='transfer'?'Transferir atendimento':'Juntar mesas'}</button></div>
</form></section></div>}
    {qrTable&&<div className="modal-backdrop" role="presentation" onMouseDown={()=>setQrTable(null)}><section className="modal-card qr-modal" role="dialog" aria-modal="true" onMouseDown={event=>event.stopPropagation()}><header className="no-print"><div><span className="eyebrow dark">ACESSO DO CLIENTE</span><h2>QR Code · Mesa {qrTable.number}</h2></div><button className="modal-close" onClick={()=>setQrTable(null)} aria-label="Fechar"><X/></button></header><div className="qr-print-card"><span className="eyebrow dark">GONÇALO MUSIC BAR</span><h2>Mesa {qrTable.number}</h2><img src={`https://api.qrserver.com/v1/create-qr-code/?size=360x360&format=svg&data=${encodeURIComponent(guestLink(qrTable))}`} alt={`QR Code da mesa ${qrTable.number}`}/><strong>{qrTable.status==='closed'?'Mesa fechada no momento':'Aponte a câmera para pedir'}</strong><p>{qrTable.status==='closed'?'Reabra a mesa no sistema antes de iniciar um novo atendimento.':'Veja o cardápio, faça seu pedido e solicite a conta.'}</p></div><div className="form-actions no-print"><button className="ghost-button" onClick={()=>void copyLink()}><Copy size={17}/>{copied?'Link copiado':'Copiar link'}</button><button className="primary-button" onClick={()=>window.print()}><Printer size={17}/> Imprimir QR</button></div></section></div>}
  </>
}